import { useCallback, useEffect, useState } from "react";
import { Layout } from "@/components/Layout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { getNavFor } from "@/lib/nav";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { UserPlus, Building2, Trash2, ShieldCheck, Users, KeyRound, Eye, EyeOff, Copy } from "lucide-react";

const PERMISSIONS = [
  ["manage_resources", "Manage resources"],
  ["manage_web_sources", "Manage web sources"],
  ["manage_rules", "Manage rules & display"],
  ["view_analytics", "View analytics"],
  ["view_activity", "View activity log"],
  ["test_dispenser", "Test dispenser view"],
];

export default function AdminConsole() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [openUser, setOpenUser] = useState(false);
  const [openBranch, setOpenBranch] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "dispenser", branch_id: "", department: "", permissions: PERMISSIONS.map((p) => p[0]) });
  const [branchForm, setBranchForm] = useState({ name: "", location: "" });
  const [creds, setCreds] = useState(null);
  const [reveal, setReveal] = useState(false);

  const load = useCallback(async () => {
    const [u, b, c] = await Promise.all([api.get("/admin/users"), api.get("/branches"), api.get("/admin/credentials")]);
    setUsers(u.data); setBranches(b.data); setCreds(c.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const copy = (text) => { navigator.clipboard?.writeText(text); toast.success("Copied"); };

  const createUser = async () => {
    try {
      const payload = { ...form };
      if (payload.role === "dispenser") { payload.permissions = []; payload.department = null; }
      else { payload.branch_id = null; }
      if (!payload.branch_id) payload.branch_id = null;
      await api.post("/admin/users", payload);
      toast.success("User created");
      setOpenUser(false);
      setForm({ name: "", email: "", password: "", role: "dispenser", branch_id: "", department: "", permissions: PERMISSIONS.map((p) => p[0]) });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const toggleActive = async (u) => {
    await api.put(`/admin/users/${u.id}`, { is_active: !u.is_active });
    load();
  };
  const delUser = async (u) => {
    if (!window.confirm(`Delete ${u.name}?`)) return;
    try { await api.delete(`/admin/users/${u.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const createBranch = async () => {
    try { await api.post("/admin/branches", branchForm); toast.success("Branch created"); setOpenBranch(false); setBranchForm({ name: "", location: "" }); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const delBranch = async (b) => {
    if (!window.confirm(`Delete ${b.name}?`)) return;
    await api.delete(`/admin/branches/${b.id}`); load();
  };

  const togglePerm = (p) => setForm((f) => ({ ...f, permissions: f.permissions.includes(p) ? f.permissions.filter((x) => x !== p) : [...f.permissions, p] }));
  const branchName = (id) => branches.find((b) => b.id === id)?.name || "—";

  return (
    <Layout nav={getNavFor(user)} showTestButton>
      <h1 className="font-heading text-3xl font-extrabold tracking-tight text-foreground">Super Admin Console</h1>
      <p className="mt-1 text-sm text-muted-foreground">Provision HODs, branch accounts and permissions for the platform.</p>

      <Tabs defaultValue="users" className="mt-8">
        <TabsList data-testid="admin-tabs">
          <TabsTrigger value="users" data-testid="tab-users"><Users className="mr-1.5 h-4 w-4" />Users</TabsTrigger>
          <TabsTrigger value="branches" data-testid="tab-branches"><Building2 className="mr-1.5 h-4 w-4" />Branches</TabsTrigger>
          <TabsTrigger value="credentials" data-testid="tab-credentials"><KeyRound className="mr-1.5 h-4 w-4" />Credentials</TabsTrigger>
        </TabsList>

        <TabsContent value="users" className="mt-6">
          <div className="mb-4 flex justify-end">
            <Dialog open={openUser} onOpenChange={setOpenUser}>
              <DialogTrigger asChild><Button data-testid="super-admin-add-hod-button"><UserPlus className="mr-1.5 h-4 w-4" />Add user</Button></DialogTrigger>
              <DialogContent className="max-h-[90vh] overflow-y-auto canda-scroll">
                <DialogHeader><DialogTitle className="font-heading">New user</DialogTitle></DialogHeader>
                <div className="space-y-4 py-2">
                  <div className="space-y-2"><Label>Full name / desk name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-name-input" placeholder="e.g. Entebbe Dispenser Desk" /></div>
                  <div className="space-y-2"><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-email-input" placeholder="account@candapharmacy.com" /></div>
                  <div className="space-y-2"><Label>Password</Label><Input value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="user-password-input" placeholder="Set a password" /></div>
                  <div className="space-y-2"><Label>Role</Label>
                    <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                      <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="dispenser">Dispenser (branch account)</SelectItem>
                        <SelectItem value="hod">Head of Department</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {form.role === "dispenser" && (
                    <div className="space-y-2"><Label>Branch</Label>
                      <Select value={form.branch_id} onValueChange={(v) => setForm({ ...form, branch_id: v })}>
                        <SelectTrigger data-testid="user-branch-select"><SelectValue placeholder="Select branch" /></SelectTrigger>
                        <SelectContent>{branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  )}
                  {form.role === "hod" && (
                    <>
                      <div className="space-y-2"><Label>Department</Label><Input value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} placeholder="e.g. Quality Assurance & Regulatory" /></div>
                      <div className="space-y-2"><Label>Permissions</Label>
                        <div className="space-y-2 rounded-lg border border-border p-3">
                          {PERMISSIONS.map(([p, lbl]) => (
                            <div key={p} className="flex items-center justify-between">
                              <span className="text-sm">{lbl}</span>
                              <Switch checked={form.permissions.includes(p)} onCheckedChange={() => togglePerm(p)} data-testid={`perm-${p}`} />
                            </div>
                          ))}
                        </div>
                      </div>
                    </>
                  )}
                </div>
                <DialogFooter><Button onClick={createUser} data-testid="create-user-submit">Create user</Button></DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
          <Card className="overflow-hidden">
            <Table>
              <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Role</TableHead><TableHead>Branch / Dept</TableHead><TableHead>Active</TableHead><TableHead></TableHead></TableRow></TableHeader>
              <TableBody>
                {users.map((u) => (
                  <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                    <TableCell className="font-medium">{u.name}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{u.email}</TableCell>
                    <TableCell><Badge variant={u.role === "super_admin" ? "default" : "outline"} className="capitalize">{u.role.replace("_", " ")}</Badge></TableCell>
                    <TableCell className="text-sm">{u.role === "dispenser" ? branchName(u.branch_id) : u.department || "—"}</TableCell>
                    <TableCell><Switch checked={u.is_active !== false} disabled={u.role === "super_admin"} onCheckedChange={() => toggleActive(u)} data-testid={`toggle-active-${u.id}`} /></TableCell>
                    <TableCell className="text-right">{u.role !== "super_admin" && <Button variant="ghost" size="icon" onClick={() => delUser(u)} data-testid={`delete-user-${u.id}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        <TabsContent value="branches" className="mt-6">
          <div className="mb-4 flex justify-end">
            <Dialog open={openBranch} onOpenChange={setOpenBranch}>
              <DialogTrigger asChild><Button data-testid="add-branch-button"><Building2 className="mr-1.5 h-4 w-4" />Add branch</Button></DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle className="font-heading">New branch</DialogTitle></DialogHeader>
                <div className="space-y-4 py-2">
                  <div className="space-y-2"><Label>Branch name</Label><Input value={branchForm.name} onChange={(e) => setBranchForm({ ...branchForm, name: e.target.value })} data-testid="branch-name-input" /></div>
                  <div className="space-y-2"><Label>Location</Label><Input value={branchForm.location} onChange={(e) => setBranchForm({ ...branchForm, location: e.target.value })} data-testid="branch-location-input" /></div>
                </div>
                <DialogFooter><Button onClick={createBranch} data-testid="create-branch-submit">Create branch</Button></DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {branches.map((b) => (
              <Card key={b.id} className="p-5" data-testid={`branch-card-${b.id}`}>
                <div className="flex items-start justify-between">
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-sky-50 text-sky-600 dark:bg-sky-950"><Building2 className="h-5 w-5" /></div>
                  <Button variant="ghost" size="icon" onClick={() => delBranch(b)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                </div>
                <div className="mt-3 font-heading text-lg font-bold text-foreground">{b.name}</div>
                <div className="text-sm text-muted-foreground">{b.location || "Uganda"}</div>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="credentials" className="mt-6">
          <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-800 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-300">
              <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0" />
              <span>Private reference for the Super Admin only. These are the standing accounts and each branch's Operations Desk password. Keep them confidential.</span>
            </div>
            <Button variant="outline" onClick={() => setReveal((v) => !v)} data-testid="creds-reveal-toggle" className="shrink-0">
              {reveal ? <><EyeOff className="mr-1.5 h-4 w-4" />Hide passwords</> : <><Eye className="mr-1.5 h-4 w-4" />Reveal passwords</>}
            </Button>
          </div>

          <Card className="overflow-hidden" data-testid="creds-accounts-card">
            <div className="border-b border-border px-5 py-3 font-heading text-sm font-bold text-foreground">Standing accounts</div>
            <Table>
              <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Role</TableHead><TableHead>Email</TableHead><TableHead>Scope</TableHead><TableHead>Password</TableHead></TableRow></TableHeader>
              <TableBody>
                {(creds?.accounts || []).map((a) => (
                  <TableRow key={a.email} data-testid={`creds-account-${a.email}`}>
                    <TableCell className="font-medium">{a.name}</TableCell>
                    <TableCell><Badge variant={a.role === "Super Admin" ? "default" : "outline"}>{a.role}</Badge></TableCell>
                    <TableCell className="text-sm text-muted-foreground">{a.email}</TableCell>
                    <TableCell className="text-sm">{a.scope}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <span className="font-mono-c text-sm" data-testid={`creds-pw-${a.email}`}>{reveal ? a.password : "••••••••"}</span>
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => copy(a.password)} data-testid={`creds-copy-${a.email}`}><Copy className="h-3.5 w-3.5" /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>

          <Card className="mt-6 overflow-hidden" data-testid="creds-ops-card">
            <div className="border-b border-border px-5 py-3 font-heading text-sm font-bold text-foreground">Branch Operations Desk passwords</div>
            <Table>
              <TableHeader><TableRow><TableHead>Branch</TableHead><TableHead>Operations Desk password</TableHead></TableRow></TableHeader>
              <TableBody>
                {(creds?.ops_desk || []).length === 0 && <TableRow><TableCell colSpan={2} className="py-6 text-center text-sm text-muted-foreground">No branches yet.</TableCell></TableRow>}
                {(creds?.ops_desk || []).map((o) => (
                  <TableRow key={o.branch_id} data-testid={`creds-ops-${o.branch_id}`}>
                    <TableCell className="font-medium">{o.branch_name}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <span className="font-mono-c text-sm">{reveal ? o.ops_password : "••••••••"}</span>
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => copy(o.ops_password)} data-testid={`creds-ops-copy-${o.branch_id}`}><Copy className="h-3.5 w-3.5" /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <p className="px-5 py-3 text-xs text-muted-foreground">Set a branch's Operations Desk password from Roles Tracker → Admin → Shift Times (Super Admin).</p>
          </Card>
        </TabsContent>
      </Tabs>
    </Layout>
  );
}
